import { useState, useEffect, useCallback, useMemo } from 'react';
import { API, formatWeight, formatDateTime, naturalBoxSort, sortBoxesNatural, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, GitMerge, Scissors, History, Search, X, Plus, Pencil, Trash2 } from 'lucide-react';

const sanitizeDecimal = (v) => { let c = v.replace(/[^0-9.]/g, ''); const p = c.split('.'); if (p.length > 2) c = p[0] + '.' + p.slice(1).join(''); return c; };

function BoxPicker({ boxes, search, setSearch, onSelect, selectedId, label }) {
  const filtered = boxes.filter(b => b.box_id.toLowerCase().includes(search.toLowerCase()));
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{label}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="relative"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input className="pl-8 h-8 text-xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <div className="space-y-1.5 max-h-[350px] overflow-y-auto">
          {filtered.map(box => (
            <div key={box.box_id} onClick={() => onSelect(box)}
              className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedId === box.box_id ? 'border-primary bg-accent/50' : 'border-border hover:border-primary/40'}`}
              data-testid={`picker-box-${box.box_id}`}>
              <div className="flex items-center gap-2 mb-1"><span className="font-mono font-semibold text-sm">{box.box_id}</span><span className={`status-badge purity-${box.gold_purity} text-[10px] py-0 px-1.5`}>{box.gold_purity}</span></div>
              <div className="text-xs text-muted-foreground">Gold: <b className="text-foreground">{formatWeight(box.gold_weight_g)}</b> | Poly: <b className="text-foreground">{formatWeight(box.polyethene_weight_g)}</b></div>
              {(box.item_names || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{box.item_names.map((n, i) => <span key={i} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">{n}</span>)}</div>}
            </div>
          ))}
          {filtered.length === 0 && <p className="text-xs text-center py-4 text-muted-foreground">No boxes</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// Multiple weight entries with + button and running total
function WeightEntries({ entries, setEntries, label, testIdPrefix }) {
  const total = useMemo(() => entries.reduce((s, v) => s + (parseFloat(v) || 0), 0), [entries]);
  return (
    <div>
      <label className="text-xs font-medium mb-1.5 block">{label}</label>
      {entries.map((val, idx) => (
        <div key={idx} className="flex items-center gap-2 mb-1.5">
          <DecimalInput value={val} onChange={v => {
            const updated = [...entries]; updated[idx] = v; setEntries(updated);
          }} placeholder="0.000" className="h-8 text-xs flex-1" data-testid={`${testIdPrefix}-${idx}`} />
          <button onClick={() => setEntries(entries.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive shrink-0"><X size={14} /></button>
        </div>
      ))}
      <div className="flex items-center justify-between mt-1">
        <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => setEntries([...entries, ''])} data-testid={`${testIdPrefix}-add`}>
          <Plus size={12} className="mr-1" />Add
        </Button>
        {entries.length > 0 && <span className="text-xs font-mono font-semibold" style={{ color: 'hsl(43, 96%, 56%)' }}>Total: {total.toFixed(3)}g</span>}
      </div>
    </div>
  );
}

// Item transfer with F/P radio
function ItemTransferList({ sourceItems, transfers, setTransfers }) {
  const toggleItem = (name) => {
    setTransfers(prev => {
      const existing = prev.find(t => t.item_name === name);
      if (existing) return prev.filter(t => t.item_name !== name);
      return [...prev, { item_name: name, mode: 'full' }];
    });
  };
  const setMode = (name, mode) => {
    setTransfers(prev => prev.map(t => t.item_name === name ? { ...t, mode } : t));
  };
  if (sourceItems.length === 0) return <p className="text-xs text-muted-foreground">No items in source box</p>;
  const sortedItems = [...sourceItems].sort((a, b) => {
    const aS = transfers.find(t => t.item_name === a) ? 0 : 1;
    const bS = transfers.find(t => t.item_name === b) ? 0 : 1;
    return aS - bS;
  });
  return (
    <div>
      <label className="text-xs font-medium mb-1.5 block">Item Names Transfer</label>
      <div className="space-y-1 p-2 rounded-lg border bg-muted/30">
        {sortedItems.map(name => {
          const selected = transfers.find(t => t.item_name === name);
          return (
            <div key={name} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50">
              <Checkbox checked={!!selected} onCheckedChange={() => toggleItem(name)} />
              <span className="text-sm flex-1">{name}</span>
              {selected && (
                <div className="flex items-center gap-1 text-xs">
                  <label className={`flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer border ${selected.mode === 'full' ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`}>
                    <input type="radio" name={`mode-${name}`} checked={selected.mode === 'full'} onChange={() => setMode(name, 'full')} className="sr-only" />F
                  </label>
                  <label className={`flex items-center gap-1 px-2 py-0.5 rounded cursor-pointer border ${selected.mode === 'partial' ? 'bg-primary/10 border-primary text-primary' : 'border-border'}`}>
                    <input type="radio" name={`mode-${name}`} checked={selected.mode === 'partial'} onChange={() => setMode(name, 'partial')} className="sr-only" />P
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">F = Full (removes from source) | P = Partial (keeps in both)</p>
    </div>
  );
}

export default function BoxMergePage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [boxes, setBoxes] = useState([]);
  const [mergeSearch, setMergeSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [sourceBox, setSourceBox] = useState(null);
  const [targetBox, setTargetBox] = useState(null);
  const [itemTransfers, setItemTransfers] = useState([]);
  const [goldEntries, setGoldEntries] = useState(['']);
  const [polyEntries, setPolyEntries] = useState(['']);
  const [newBoxId, setNewBoxId] = useState('');
  // Split
  const [splitSource, setSplitSource] = useState(null);
  const [splitNewBoxId, setSplitNewBoxId] = useState('');
  const [splitItemTransfers, setSplitItemTransfers] = useState([]);
  const [splitGoldEntries, setSplitGoldEntries] = useState(['']);
  const [splitPolyEntries, setSplitPolyEntries] = useState(['']);
  const [splitPurity, setSplitPurity] = useState('');
  const [splitSearch, setSplitSearch] = useState('');
  const [mergeHistory, setMergeHistory] = useState([]);
  const { busy: loading, run } = useSubmitGuard('Please wait — the previous action is still processing');
  const [activeTab, setActiveTab] = useState('transfer');
  const [historySearch, setHistorySearch] = useState('');
  const [editingDateId, setEditingDateId] = useState(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [editingTxnId, setEditingTxnId] = useState(null);
  const [editGoldValue, setEditGoldValue] = useState('');
  const [editPolyValue, setEditPolyValue] = useState('');
  const [deletingTxnId, setDeletingTxnId] = useState(null);

  const filteredMergeHistory = useMemo(() => {
    if (!historySearch) return mergeHistory;
    const q = historySearch.toLowerCase();
    return mergeHistory.filter(ev => ev.box_id?.toLowerCase().includes(q) || ev.notes?.toLowerCase().includes(q));
  }, [mergeHistory, historySearch]);

  const fetchBoxes = useCallback(async () => { try { setBoxes(sortBoxesNatural((await API.get('/boxes', { params: { active_only: true } })).data)); } catch(e) {} }, []);
  const fetchHistory = useCallback(async () => { try { setMergeHistory((await API.get('/box-merge/history')).data); } catch(e) {} }, []);
  useEffect(() => { fetchBoxes(); fetchHistory(); }, [fetchBoxes, fetchHistory]);

  const handleChangeMergeDate = async (mergeOrSplitId) => {
    if (!editDateValue) { toast.error('Enter a valid date'); return; }
    return run(async () => {
      try {
        await API.put('/box-merge/change-date', { merge_id: mergeOrSplitId, new_date: editDateValue });
        toast.success('Date updated');
        setEditingDateId(null);
        setEditDateValue('');
        fetchHistory();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change date'); }
    });
  };

  const handleEditTransaction = async (mergeOrSplitId) => {
    const goldVal = parseFloat(editGoldValue);
    const polyVal = parseFloat(editPolyValue);
    if (isNaN(goldVal) && isNaN(polyVal)) { toast.error('Enter valid weight values'); return; }
    return run(async () => {
      try {
        const payload = { merge_id: mergeOrSplitId };
        if (!isNaN(goldVal)) payload.new_gold_weight_g = goldVal;
        if (!isNaN(polyVal)) payload.new_poly_weight_g = polyVal;
        await API.put('/box-merge/edit-transaction', payload);
        toast.success('Transaction updated');
        setEditingTxnId(null);
        setEditGoldValue('');
        setEditPolyValue('');
        await Promise.all([fetchBoxes(), fetchHistory()]);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to edit transaction'); }
    });
  };

  const handleDeleteTransaction = async (mergeOrSplitId) => {
    return run(async () => {
      try {
        await API.delete(`/box-merge/delete-transaction/${mergeOrSplitId}`);
        toast.success('Transaction deleted & reversed');
        setDeletingTxnId(null);
        await Promise.all([fetchBoxes(), fetchHistory()]);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete transaction'); }
    });
  };

  const handleTransfer = async () => {
    if (!sourceBox || !targetBox) { toast.error('Select both boxes'); return; }
    const golds = goldEntries.map(v => parseFloat(v) || 0).filter(v => v > 0);
    const polys = polyEntries.map(v => parseFloat(v) || 0).filter(v => v > 0);
    if (itemTransfers.length === 0 && golds.length === 0 && polys.length === 0) { toast.error('Add items, gold, or poly to transfer'); return; }
    return run(async () => {
      try {
        await API.post('/box-merge', {
          source_box_id: sourceBox.box_id, target_box_id: targetBox.box_id,
          item_transfers: itemTransfers, gold_transfers: golds, poly_transfers: polys,
          new_box_id: newBoxId || targetBox.box_id,
          submitted_by: user?.role || 'stock_manager',
        });
        toast.success('Transfer completed!');
        setItemTransfers([]); setGoldEntries(['']); setPolyEntries(['']); setNewBoxId('');
        setSourceBox(null); setTargetBox(null);
        await Promise.all([fetchBoxes(), fetchHistory()]);
      } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handleSplit = async () => {
    if (!splitSource) { toast.error('Select a source box'); return; }
    if (!splitNewBoxId) { toast.error('Enter new box ID'); return; }
    const golds = splitGoldEntries.map(v => parseFloat(v) || 0).filter(v => v > 0);
    const polys = splitPolyEntries.map(v => parseFloat(v) || 0).filter(v => v > 0);
    if (splitItemTransfers.length === 0 && golds.length === 0 && polys.length === 0) { toast.error('Add items, gold, or poly to split'); return; }
    return run(async () => {
      try {
        await API.post('/box-split', {
          source_box_id: splitSource.box_id, new_box_id: splitNewBoxId,
          gold_purity: splitPurity || splitSource.gold_purity,
          item_transfers: splitItemTransfers, gold_transfers: golds, poly_transfers: polys,
          submitted_by: user?.role || 'stock_manager',
        });
        toast.success('Split completed!');
        setSplitItemTransfers([]); setSplitGoldEntries(['']); setSplitPolyEntries(['']); setSplitNewBoxId(''); setSplitPurity('');
        setSplitSource(null);
        await Promise.all([fetchBoxes(), fetchHistory()]);
      } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  return (
    <div data-testid="box-merge-page">
      <div className="page-header"><h1>Box Merging & Splitting</h1><p className="hidden sm:block">Transfer items & weights between boxes</p></div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:flex mb-4">
          <TabsTrigger value="transfer" className="text-xs sm:text-sm flex items-center gap-1.5"><GitMerge size={14} /> Transfer</TabsTrigger>
          <TabsTrigger value="split" className="text-xs sm:text-sm flex items-center gap-1.5"><Scissors size={14} /> Split</TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm flex items-center gap-1.5"><History size={14} /> History</TabsTrigger>
        </TabsList>

        {/* TRANSFER */}
        <TabsContent value="transfer">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-3"><BoxPicker boxes={boxes.filter(b => b.box_id !== targetBox?.box_id)} search={mergeSearch} setSearch={setMergeSearch} onSelect={b => setSourceBox(sourceBox?.box_id === b.box_id ? null : b)} selectedId={sourceBox?.box_id} label="Source Box" /></div>
            <div className="lg:col-span-6">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Transfer Details</CardTitle></CardHeader><CardContent>
                {sourceBox && targetBox ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm"><Badge variant="outline" className="font-mono">{sourceBox.box_id}</Badge><ArrowRight size={16} className="text-primary" /><Badge variant="outline" className="font-mono">{targetBox.box_id}</Badge></div>
                    <ItemTransferList sourceItems={sourceBox.item_names || []} transfers={itemTransfers} setTransfers={setItemTransfers} />
                    <WeightEntries entries={goldEntries} setEntries={setGoldEntries} label="Gold Weight Transfers (g)" testIdPrefix="merge-gold" />
                    <WeightEntries entries={polyEntries} setEntries={setPolyEntries} label="Polythene Weight Transfers (g)" testIdPrefix="merge-poly" />
                    <div><label className="text-xs font-medium mb-1 block">Rename Target Box (optional)</label><Input value={newBoxId} onChange={e => setNewBoxId(e.target.value)} placeholder="Keep target ID" className="font-mono h-8 text-xs" /></div>
                    <Button onClick={handleTransfer} disabled={loading} className="w-full" data-testid="boxmerge-confirm-button"><GitMerge size={16} className="mr-2" />{loading ? 'Transferring...' : 'Transfer'}</Button>
                  </div>
                ) : <div className="text-center py-8 text-muted-foreground"><GitMerge size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Select a source and target box</p></div>}
              </CardContent></Card>
            </div>
            <div className="lg:col-span-3"><BoxPicker boxes={boxes.filter(b => b.box_id !== sourceBox?.box_id)} search={targetSearch} setSearch={setTargetSearch} onSelect={b => setTargetBox(targetBox?.box_id === b.box_id ? null : b)} selectedId={targetBox?.box_id} label="Target Box" /></div>
          </div>
        </TabsContent>

        {/* SPLIT */}
        <TabsContent value="split">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-3"><BoxPicker boxes={boxes} search={splitSearch} setSearch={setSplitSearch} onSelect={b => setSplitSource(splitSource?.box_id === b.box_id ? null : b)} selectedId={splitSource?.box_id} label="Source Box" /></div>
            <div className="lg:col-span-9">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Scissors size={16} />Split Box</CardTitle></CardHeader><CardContent>
                {splitSource ? (
                  <div className="space-y-4">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="font-mono font-semibold text-sm mb-1">{splitSource.box_id} <span className={`status-badge purity-${splitSource.gold_purity} text-xs`}>{splitSource.gold_purity}</span></p>
                      <div className="flex gap-3 text-xs text-muted-foreground">
                        <span>Gold: <b className="text-foreground">{formatWeight(splitSource.gold_weight_g)}</b></span>
                        <span>Poly: <b className="text-foreground">{formatWeight(splitSource.polyethene_weight_g)}</b></span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="text-xs font-medium mb-1 block">New Box ID *</label><Input value={splitNewBoxId} onChange={e => setSplitNewBoxId(e.target.value)} placeholder="e.g. BOX-NEW" className="font-mono" data-testid="split-new-box-id" /></div>
                      <div><label className="text-xs font-medium mb-1 block">Purity</label>
                        <Select value={splitPurity || splitSource.gold_purity} onValueChange={setSplitPurity}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="76">76</SelectItem><SelectItem value="84">84</SelectItem><SelectItem value="92">92</SelectItem></SelectContent></Select>
                      </div>
                    </div>
                    <ItemTransferList sourceItems={splitSource.item_names || []} transfers={splitItemTransfers} setTransfers={setSplitItemTransfers} />
                    <WeightEntries entries={splitGoldEntries} setEntries={setSplitGoldEntries} label="Gold Weight to Split (g)" testIdPrefix="split-gold" />
                    <WeightEntries entries={splitPolyEntries} setEntries={setSplitPolyEntries} label="Polythene Weight to Split (g)" testIdPrefix="split-poly" />
                    <Button onClick={handleSplit} disabled={loading} className="w-full" data-testid="split-confirm-button"><Scissors size={16} className="mr-2" />{loading ? 'Splitting...' : 'Split Box'}</Button>
                  </div>
                ) : <div className="text-center py-12 text-muted-foreground"><Scissors size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm">Select a source box to split</p></div>}
              </CardContent></Card>
            </div>
          </div>
        </TabsContent>

        {/* HISTORY */}
        <TabsContent value="history">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Merge & Split History</CardTitle></CardHeader><CardContent>
            <div className="mb-3">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8 h-9 text-xs"
                  placeholder="Search by box ID..."
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                  data-testid="merge-history-search"
                />
                {historySearch && (
                  <button onClick={() => setHistorySearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {filteredMergeHistory.map(ev => {
                const txnId = ev.merge_id || ev.split_id;
                return (
                <div key={ev.id} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-center justify-between mb-1"><Badge variant={ev.operation_type.includes('merge') ? 'default' : 'secondary'} className="text-xs">{ev.operation_type.replace(/_/g, ' ')}</Badge><span className="text-xs text-muted-foreground">{formatDateTime(ev.created_at)}</span></div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-mono text-xs">Box: {ev.box_id} | Date: <span className="font-semibold">{displayDate(ev.date)}</span></p>
                    {isAdmin && txnId && (
                      <div className="flex flex-col items-end gap-1">
                        {/* Edit Date */}
                        {editingDateId === txnId ? (
                          <div className="flex items-center gap-1">
                            <Input type="date" value={editDateValue} onChange={e => setEditDateValue(e.target.value)} className="h-6 w-32 text-xs" data-testid="merge-date-input" />
                            <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => handleChangeMergeDate(txnId)} data-testid="merge-date-save">Save</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingDateId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-1" onClick={() => { setEditingDateId(txnId); setEditDateValue(ev.date || ''); }} data-testid="merge-date-edit-btn">Edit Date</Button>
                        )}
                        {/* Edit Transaction */}
                        {editingTxnId === txnId ? (
                          <div className="flex items-center gap-1 flex-wrap justify-end">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground">Gold:</span>
                              <Input type="number" step="0.001" value={editGoldValue} onChange={e => setEditGoldValue(e.target.value)} className="h-6 w-24 text-xs" placeholder="Gold (g)" data-testid="edit-txn-gold-input" />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground">Poly:</span>
                              <Input type="number" step="0.001" value={editPolyValue} onChange={e => setEditPolyValue(e.target.value)} className="h-6 w-24 text-xs" placeholder="Poly (g)" data-testid="edit-txn-poly-input" />
                            </div>
                            <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => handleEditTransaction(txnId)} data-testid="edit-txn-save">Save</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setEditingTxnId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-1 gap-1" onClick={() => { setEditingTxnId(txnId); setEditGoldValue(ev.gold_weight_g?.toString() || '0'); setEditPolyValue(ev.polyethene_weight_g?.toString() || '0'); }} data-testid="edit-txn-btn">
                            <Pencil size={12} /> Edit Transaction
                          </Button>
                        )}
                        {/* Delete Transaction */}
                        {deletingTxnId === txnId ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-destructive">Confirm delete?</span>
                            <Button size="sm" variant="destructive" className="h-6 text-xs px-2" onClick={() => handleDeleteTransaction(txnId)} data-testid="delete-txn-confirm">Yes, Delete</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => setDeletingTxnId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-1 gap-1 text-destructive hover:text-destructive" onClick={() => setDeletingTxnId(txnId)} data-testid="delete-txn-btn">
                            <Trash2 size={12} /> Delete Transaction
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Before/After weights for this box */}
                  {(ev.before_gross_g != null || ev.after_gross_g != null) && (
                    <div className="flex items-center gap-1.5 text-xs font-mono mb-1">
                      <span className="font-semibold text-muted-foreground">{ev.box_id}:</span>
                      <span className="text-muted-foreground/70">Before:</span>
                      <span className="font-semibold">{formatWeight(ev.before_gross_g)}</span>
                      <span className="text-muted-foreground/50">&rarr;</span>
                      <span className="text-muted-foreground/70">After:</span>
                      <span className="font-semibold">{formatWeight(ev.after_gross_g)}</span>
                      {ev.before_gross_g != null && ev.after_gross_g != null && ev.before_gross_g !== ev.after_gross_g && (
                        <span className={`font-semibold ${ev.after_gross_g > ev.before_gross_g ? 'text-emerald-400' : 'text-rose-400'}`}>
                          ({ev.after_gross_g > ev.before_gross_g ? '+' : ''}{(ev.after_gross_g - ev.before_gross_g).toFixed(3)}g)
                        </span>
                      )}
                    </div>
                  )}
                  {/* Paired box before/after */}
                  {ev.paired_box_id && (ev.paired_before_gross_g != null || ev.paired_after_gross_g != null) && (
                    <div className="flex items-center gap-1.5 text-xs font-mono mb-1">
                      <span className="font-semibold text-muted-foreground">{ev.paired_box_id}:</span>
                      <span className="text-muted-foreground/70">Before:</span>
                      <span className="font-semibold">{formatWeight(ev.paired_before_gross_g)}</span>
                      <span className="text-muted-foreground/50">&rarr;</span>
                      <span className="text-muted-foreground/70">After:</span>
                      <span className="font-semibold">{formatWeight(ev.paired_after_gross_g)}</span>
                      {ev.paired_before_gross_g != null && ev.paired_after_gross_g != null && ev.paired_before_gross_g !== ev.paired_after_gross_g && (
                        <span className={`font-semibold ${ev.paired_after_gross_g > ev.paired_before_gross_g ? 'text-emerald-400' : 'text-rose-400'}`}>
                          ({ev.paired_after_gross_g > ev.paired_before_gross_g ? '+' : ''}{(ev.paired_after_gross_g - ev.paired_before_gross_g).toFixed(3)}g)
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-3 text-xs"><span>Gold: <b className="weight-value">{formatWeight(ev.gold_weight_g)}</b></span><span>Poly: <b className="weight-value">{formatWeight(ev.polyethene_weight_g)}</b></span></div>
                  {ev.notes && <p className="text-xs text-muted-foreground mt-1">{ev.notes}</p>}
                </div>
                );
              })}
              {filteredMergeHistory.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">{historySearch ? 'No history for this box' : 'No merge/split history'}</p>}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
